// Player simulation state and movement integration. No rendering here; the
// camera in the render layer reads pos + yaw/pitch each frame.

import {
  GRAVITY,
  JUMP_SPEED,
  PLAYER_EYE,
  PLAYER_HEIGHT,
  PLAYER_WIDTH,
  WALK_SPEED,
  WATER_DRAG,
} from './constants';
import { AABBEntity, isInWater, moveEntity } from './physics';
import { Vec3, vec3 } from './math';
import { World } from './world';

export interface InputState {
  forward: boolean;
  back: boolean;
  left: boolean;
  right: boolean;
  jump: boolean;
}

export const MAX_HEALTH = 20;

export class Player implements AABBEntity {
  pos: Vec3;
  vel = vec3(0, 0, 0);
  width = PLAYER_WIDTH;
  height = PLAYER_HEIGHT;
  onGround = false;

  yaw = 0; // radians, around Y
  pitch = 0; // radians, clamped

  health = MAX_HEALTH;
  dead = false;
  spawn: Vec3;

  /** Distance fallen since last on ground, for fall-damage accounting. */
  private fallStart = 0;
  private wasInWater = false;
  private regenTimer = 0;
  private hurtCooldown = 0;

  constructor(spawn: Vec3) {
    this.spawn = { ...spawn };
    this.pos = { ...spawn };
  }

  /** Eye position for camera + raycasting. */
  eye(): Vec3 {
    return { x: this.pos.x, y: this.pos.y + PLAYER_EYE, z: this.pos.z };
  }

  /** Forward look direction as a unit vector. */
  lookDir(): Vec3 {
    const cp = Math.cos(this.pitch);
    return {
      x: -Math.sin(this.yaw) * cp,
      y: Math.sin(this.pitch),
      z: -Math.cos(this.yaw) * cp,
    };
  }

  update(world: World, input: InputState, dt: number): void {
    if (this.dead) return;
    const inWater = isInWater(world, this);

    // Horizontal movement relative to yaw.
    const sin = Math.sin(this.yaw);
    const cos = Math.cos(this.yaw);
    let mx = 0;
    let mz = 0;
    if (input.forward) {
      mx -= sin;
      mz -= cos;
    }
    if (input.back) {
      mx += sin;
      mz += cos;
    }
    if (input.left) {
      mx -= cos;
      mz += sin;
    }
    if (input.right) {
      mx += cos;
      mz -= sin;
    }
    const len = Math.hypot(mx, mz);
    const speed = inWater ? WALK_SPEED * 0.6 : WALK_SPEED;
    if (len > 0) {
      this.vel.x = (mx / len) * speed;
      this.vel.z = (mz / len) * speed;
    } else {
      this.vel.x = 0;
      this.vel.z = 0;
    }

    // Vertical: gravity, jumping, swimming.
    if (inWater) {
      this.vel.y += -GRAVITY * WATER_DRAG * dt;
      if (input.jump) this.vel.y = WALK_SPEED * 0.8; // swim up
      this.vel.y = Math.max(this.vel.y, -WALK_SPEED); // cap sink speed
    } else {
      this.vel.y -= GRAVITY * dt;
      if (input.jump && this.onGround) this.vel.y = JUMP_SPEED;
    }

    const groundedBefore = this.onGround;
    moveEntity(world, this, dt);

    this.accountFallDamage(groundedBefore, inWater);
    this.wasInWater = inWater;

    // Passive regen.
    if (this.hurtCooldown > 0) this.hurtCooldown -= dt;
    if (this.health < MAX_HEALTH) {
      this.regenTimer += dt;
      if (this.regenTimer >= 4) {
        this.regenTimer = 0;
        this.health = Math.min(MAX_HEALTH, this.health + 1);
      }
    }

    // Fell out of the world.
    if (this.pos.y < -10) this.damage(MAX_HEALTH);
  }

  private accountFallDamage(groundedBefore: boolean, inWater: boolean): void {
    if (this.onGround && !groundedBefore) {
      const fallen = this.fallStart - this.pos.y;
      if (fallen > 3 && !inWater && !this.wasInWater) {
        this.damage(Math.floor(fallen - 3));
      }
      this.fallStart = this.pos.y;
    }
    if (this.onGround || inWater) {
      this.fallStart = this.pos.y;
    } else if (this.pos.y > this.fallStart) {
      this.fallStart = this.pos.y;
    }
  }

  damage(amount: number): void {
    if (this.dead || this.hurtCooldown > 0) return;
    this.health -= amount;
    this.hurtCooldown = 0.5;
    this.regenTimer = 0;
    if (this.health <= 0) {
      this.health = 0;
      this.dead = true;
    }
  }

  respawn(): void {
    this.pos = { ...this.spawn };
    this.vel = vec3(0, 0, 0);
    this.health = MAX_HEALTH;
    this.dead = false;
    this.fallStart = this.spawn.y;
  }
}
